import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, User, Users, Calendar, ShoppingBag, Gift, Lock, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { RewardClaim } from '@/components/RewardClaim';
import { BusinessReports } from '@/components/BusinessReports';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

export default function Business() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('personal');
  const [rewards, setRewards] = useState(null);
  const [claimReward, setClaimReward] = useState(null);

  const loadRewards = useCallback(async () => {
    try { const r = await api.get('/network/rewards'); setRewards(r.data); }
    catch { setRewards(null); }
  }, []);

  useEffect(() => {
    api.get('/business/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load your business'))
      .finally(() => setLoading(false));
    loadRewards();
  }, [loadRewards]);

  // Finish a Cashfree reward payment after the hosted-checkout redirect returns.
  useEffect(() => {
    const cfOrder = params.get('cf_verify');
    if (!cfOrder) return;
    (async () => {
      try {
        const { data } = await api.post('/store/cashfree-verify', { order_id: cfOrder });
        if (data?.status === 'paid') { toast.success('Reward claimed! 🎁'); loadRewards(); }
        else { toast('Payment is still processing — we could not confirm it yet.'); }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not confirm the payment');
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('cf_verify');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="p-6 text-sm text-[var(--bz-muted)]" data-testid="business-loading">Loading your business…</div>;
  if (!data) return <div className="p-6 text-sm text-[var(--bz-muted)]" data-testid="business-empty">You are not part of the network yet.</div>;

  const orders = tab === 'personal' ? data.personal_orders : data.team_orders;
  const rewardList = rewards?.rewards || [];
  const holdDays = rewards?.hold_days || 28;

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-5 space-y-5" data-testid="business-page">
      <div className="flex items-center gap-2">
        <Briefcase className="h-6 w-6 text-[#1D4ED8]" />
        <h1 className="text-2xl font-black display">Business</h1>
        <span className="text-xs text-[var(--bz-muted)]">· ID {data.personal_id}</span>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="stat-personal-bv">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold"><User className="h-3.5 w-3.5" />Personal BV</div>
          <div className="text-2xl font-black display text-[#1E3A8A] mt-1">{data.totals.personal_bv}</div>
        </Card>
        <Card className="p-4" data-testid="stat-personal-sales">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold"><ShoppingBag className="h-3.5 w-3.5" />Personal sales</div>
          <div className="text-2xl font-black display mt-1">{money(data.totals.personal_total)}</div>
        </Card>
        <Card className="p-4" data-testid="stat-team-bv">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-green-700 font-bold"><Users className="h-3.5 w-3.5" />Team BV</div>
          <div className="text-2xl font-black display text-green-700 mt-1">{data.totals.team_bv}</div>
        </Card>
        <Card className="p-4" data-testid="stat-team-sales">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-green-700 font-bold"><Users className="h-3.5 w-3.5" />Team sales</div>
          <div className="text-2xl font-black display text-green-700 mt-1">{money(data.totals.team_total)}</div>
        </Card>
      </div>

      {/* Free product rewards */}
      <Card className="p-4" data-testid="rewards-card">
        <div className="font-bold display mb-1 flex items-center gap-2"><Gift className="h-4 w-4 text-[var(--bz-orange)]" />Your rewards</div>
        <div className="text-[11px] text-[var(--bz-muted)] mb-3">Free products you've earned. Every reward is held for {holdDays} days from the day you earn it — it shows here immediately and can be claimed after {holdDays} days.</div>
        {rewardList.length === 0 ? (
          <div className="text-sm text-[var(--bz-muted)]" data-testid="rewards-empty">No rewards yet — hit your weekly PV target or help a referral cross the qualifying BV to earn free product rewards.</div>
        ) : (
          <div className="space-y-2" data-testid="rewards-list">
            {rewardList.map((r) => (
              <div key={r.reward_id} className="rounded-xl border border-[var(--bz-line)] p-3 flex items-center justify-between gap-3" data-testid={`reward-row-${r.reward_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">{r.title}</span>
                    {r.kind === 'weekly'
                      ? <Badge className="bg-[var(--bz-orange)] text-[10px]">Weekly</Badge>
                      : <Badge className="bg-[#1D4ED8] text-[10px]">Sponsor</Badge>}
                  </div>
                  <div className="text-[11px] text-[var(--bz-muted)] mt-0.5 truncate">{r.subtitle}</div>
                  <div className="text-[11px] text-[var(--bz-muted)] mt-0.5">Earned {fmtDate(r.earned_at)}</div>
                </div>
                <div className="shrink-0 text-right">
                  {r.status === 'available' && (
                    <Button onClick={() => setClaimReward(r)} className="rounded-full bg-[var(--bz-orange)] hover:bg-[#e65100] text-white h-8" data-testid={`reward-claim-btn-${r.reward_id}`}>Claim now</Button>
                  )}
                  {r.status === 'hold' && (
                    <div className="text-right" data-testid={`reward-hold-${r.reward_id}`}>
                      <div className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600"><Lock className="h-3 w-3" />On hold</div>
                      <div className="text-[11px] text-[var(--bz-muted)] flex items-center gap-1 justify-end mt-0.5"><Clock className="h-3 w-3" />Can be claimed in {r.days_left} day{r.days_left === 1 ? '' : 's'}</div>
                      <div className="text-[10px] text-[var(--bz-muted)]">on {fmtDate(r.claimable_at)}</div>
                    </div>
                  )}
                  {r.status === 'claimed' && <Badge className="bg-green-600 text-[10px]" data-testid={`reward-claimed-${r.reward_id}`}>Claimed</Badge>}
                  {r.status === 'expired' && <Badge className="bg-gray-400 text-[10px]" data-testid={`reward-expired-${r.reward_id}`}>Expired</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-4" data-testid="weekly-sales-card">
        <div className="font-bold display mb-3 flex items-center gap-2"><Calendar className="h-4 w-4" />Weekly sales (business week Sat–Fri)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Week of</th>
                <th className="py-1.5 pr-3">Personal orders</th>
                <th className="py-1.5 pr-3">Personal BV</th>
                <th className="py-1.5 pr-3">Personal ₹</th>
                <th className="py-1.5 pr-3">Team orders</th>
                <th className="py-1.5 pr-3">Team BV</th>
                <th className="py-1.5">Team ₹</th>
              </tr>
            </thead>
            <tbody data-testid="weekly-sales-body">
              {data.weekly.length === 0 && <tr><td colSpan={7} className="py-3 text-[var(--bz-muted)]">No sales yet.</td></tr>}
              {data.weekly.map((w) => (
                <tr key={w.week} className="border-t border-[var(--bz-line)]" data-testid={`week-row-${w.week}`}>
                  <td className="py-1.5 pr-3 font-semibold">{w.week}</td>
                  <td className="py-1.5 pr-3">{w.personal_orders}</td>
                  <td className="py-1.5 pr-3">{w.personal_bv}</td>
                  <td className="py-1.5 pr-3">{money(w.personal_total)}</td>
                  <td className="py-1.5 pr-3 text-green-700">{w.team_orders}</td>
                  <td className="py-1.5 pr-3 text-green-700">{w.team_bv}</td>
                  <td className="py-1.5 text-green-700">{money(w.team_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Date-range income + weekly business reports */}
      <BusinessReports />

      {/* Orders list */}
      <Card className="p-4" data-testid="orders-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-bold display">Orders — who bought what & when</div>
          <div className="flex gap-1 text-xs">
            {[['personal', `Personal (${data.personal_orders.length})`], ['team', `Team (${data.team_orders.length})`]].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`orders-tab-${t}`}
                className={`px-3 py-1 rounded-full font-semibold ${tab === t ? 'bg-[#1D4ED8] text-white' : 'bg-[var(--bz-soft)]'}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Who ordered</th>
                <th className="py-1.5 pr-3">Products</th>
                <th className="py-1.5 pr-3">Date</th>
                <th className="py-1.5 pr-3">Week of</th>
                <th className="py-1.5 pr-3">BV</th>
                <th className="py-1.5">Amount</th>
              </tr>
            </thead>
            <tbody data-testid="orders-body">
              {orders.length === 0 && <tr><td colSpan={6} className="py-3 text-[var(--bz-muted)]">No {tab} orders yet.</td></tr>}
              {orders.map((o) => (
                <tr key={o.store_order_id} className="border-t border-[var(--bz-line)]" data-testid={`order-row-${o.store_order_id}`}>
                  <td className="py-1.5 pr-3 font-semibold">{o.who}</td>
                  <td className="py-1.5 pr-3 max-w-[280px] truncate" title={o.products}>{o.products}</td>
                  <td className="py-1.5 pr-3">{o.date}</td>
                  <td className="py-1.5 pr-3">{o.week}</td>
                  <td className="py-1.5 pr-3">{o.bv}</td>
                  <td className="py-1.5 font-semibold">{money(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RewardClaim
        reward={claimReward}
        open={!!claimReward}
        onClose={() => setClaimReward(null)}
        onClaimed={loadRewards}
      />
    </div>
  );
}
